import TextInput, {
  type TextInputProps,
} from "@booking/components/compounds/forms/TextInput";
import {
  TextInputAutocompleteEnum,
  TextInputTypeEnum,
} from "@booking/components/compounds/forms/TextInput";
import { SitewideResultTypeEnum } from "@search/api/enums";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";

import ListItem from "@/components/atoms/ListItem";
import List from "@/components/compounds/List";
import type { CSSVariable, CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./Autocomplete.module.css";

type AutocompleteResultType = Record<string, any> & {
  id: number;
  type?: SitewideResultTypeEnum;
};
type AutocompleteResultsType = Array<AutocompleteResultType>;
type AutocompleteResultsCacheType = Record<string, AutocompleteResultsType>;

const ref = "autocomplete";

const MAX_SIZE = 12;
const NO_RESULTS = "No results";
const TIMEOUT = 350;

interface AutocompleteMeta {
  query: string;
  index: number;
}

interface AutocompleteProps
  extends Partial<Omit<CommonProps & TextInputProps, "onUpdate">> {
  keyName?: string;
  noResultsText?: string;
  onUpdate?: (selected: any, meta: AutocompleteMeta) => void;
  render?: (result: AutocompleteResultType) => React.ReactNode;
  search?: (value: string) => any;
  src?: string;
  suggestions?: AutocompleteResultsType;
  transform?: (results: any) => any;
}

function useAutocomplete(props: AutocompleteProps) {
  const {
    color,
    icon,
    iconColor,
    id = createId(ref),
    keyName = "name",
    minLength = 1,
    placeholder = "Search...",
    render = (result: AutocompleteResultType) => <p>{result.name}</p>,
    search,
    size = MAX_SIZE,
    src = "",
    suggestions,
    transform = (json: any) => json,
    onUpdate = (selected: AutocompleteResultType, _meta: AutocompleteMeta) => {
      console.log({ selected });
    },
  } = props;

  if (!search && !src) {
    throw new ReferenceError("Missing RPC method or URL");
  }

  const className = clsx(styles[ref], color && styles[color], props.className);

  return {
    className,
    icon,
    iconColor,
    id,
    keyName,
    minLength,
    placeholder,
    render,
    search,
    src,
    size,
    suggestions,
    transform,
    onUpdate,
  };
}

function Autocomplete(props: AutocompleteProps) {
  const {
    className,
    id,
    keyName,
    minLength,
    onUpdate,
    placeholder,
    render,
    search,
    size,
    src,
    suggestions,
    transform,
  } = useAutocomplete(props);
  const inputRef = useRef<HTMLInputElement>(null);
  const ulRef = useRef<HTMLUListElement>(null);
  const justSelected = useRef(false);
  const style: CSSVariable = { "--max-h": `calc(${size} * 2rem)` };

  const [cache, setCache] = useState<AutocompleteResultsCacheType>({});
  const [results, setResults] = useState<AutocompleteResultsType>([]);
  const [selected, setSelected] = useState<AutocompleteResultType | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [showResults, setShowResults] = useState(false);
  const [value, setValue] = useState("");

  async function fetchResults() {
    if (value.length >= minLength) {
      const lower = value.toLowerCase();
      let results = cache[lower];

      console.log(
        `${results ? "Cache hit" : "API call"}: "${search ? lower : value}"`,
      );
      if (!results) {
        try {
          let json;

          if (search) {
            json = await search(lower);
          } else {
            const url = new URL(src.replace("%s", value));
            const response = await fetch(url);

            if (!response.ok) {
              throw new Error(`Failed to fetch: ${url}`);
            }

            json = await response.json();
          }

          results = transform(json);
        } catch {
          results = [];
        } finally {
          results = results.slice(0, size);
          setCache((cache) => ({ ...cache, [lower]: [...results] }));
        }
      }

      setResults(results);
      setSelectedIndex(-1);
      setShowResults(true);
    }
  }

  function onafterselect(result: AutocompleteResultType, index: number) {
    justSelected.current = true;
    setValue(result[keyName]);
    setSelected(result);
    setShowResults(false);
    onUpdate(result, { query: value, index });
  }

  function onblur() {
    if (!results.length) {
      setShowResults(false);
    }
  }

  function onclick(e: any) {
    const { current: input } = inputRef;

    if (input && (e.target as HTMLElement) !== input) {
      setShowResults(false);
    }
  }

  function onfocus() {
    if (!value) {
      setResults(suggestions || []);
    }
    setShowResults(true);
  }

  function oninput(e: React.BaseSyntheticEvent) {
    setValue(e.target.value);
  }

  function onkeydown(e: React.KeyboardEvent<HTMLInputElement>) {
    const { key } = e;

    if (["ArrowDown", "ArrowUp", "Escape", "Enter"].includes(key)) {
      e.preventDefault();

      const doScroll = /^Arrow/.test(key);
      const { length } = results;
      let newIndex = selectedIndex;

      if (key === "ArrowDown") {
        newIndex = selectedIndex < length - 1 ? selectedIndex + 1 : 0;
      } else if (key === "ArrowUp") {
        newIndex =
          selectedIndex === 0 ? length - 1 : Math.max(selectedIndex - 1, 0);
      } else if (key === "Enter" && selectedIndex > -1) {
        onafterselect(results[selectedIndex], selectedIndex);
      } else {
        // Escape
        if (!showResults) {
          inputRef.current?.blur();
        } else {
          setShowResults(false);
        }

        setSelectedIndex(-1);
      }

      if (doScroll) {
        setSelectedIndex(newIndex);
        setShowResults(length > 0);
        const li = ulRef.current?.querySelector(
          `li:nth-child(${newIndex + 1})`,
        );

        li && li.scrollIntoView(false);
      }
    }
  }

  function onselect(e: React.MouseEvent<HTMLLIElement>) {
    const li = (e.target as HTMLElement).closest("li");

    if (li) {
      const { index } = li.dataset;

      if (index) {
        const selectedIndex = parseFloat(index);
        const result = results[selectedIndex];

        setSelectedIndex(selectedIndex);
        onafterselect(result, selectedIndex);
      }
    }
  }

  function renderNoResults() {
    const { noResultsText } = props;

    return cache[value] && !results.length ? (
      <section className={styles["no-results"]}>
        {noResultsText || NO_RESULTS}
      </section>
    ) : null;
  }

  function renderResults() {
    const isShowingSuggestions = value.length === 0;
    return showResults && results.length ? (
      <section
        className={clsx(
          styles.results,
          isShowingSuggestions && "showing-suggestions",
        )}
        style={style}
        aria-live={"polite"}
      >
        <List ref={ulRef} compact role={"listbox"}>
          {results.map((result, index) => (
            <ListItem
              key={`${id}_${index}`}
              className={clsx(
                result.id != null &&
                  selected?.id === result.id &&
                  styles.selected,
                index === selectedIndex && styles.hover,
              )}
              data-index={index}
              onClick={onselect}
            >
              {render(result)}
            </ListItem>
          ))}
        </List>
      </section>
    ) : null;
  }

  useEffect(() => {
    if (justSelected.current) {
      justSelected.current = false;
      return;
    }

    // eslint-disable-next-line no-undef
    let timeout: NodeJS.Timeout;

    if (value.length === 0) {
      setResults(suggestions || []);
      setSelected(null);
      setSelectedIndex(-1);
    } else if (value.length > 0 && value.length < minLength) {
      setResults([]);
    } else {
      timeout = setTimeout(fetchResults, TIMEOUT);
    }

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, suggestions]);

  useEffect(() => {
    const { current } = inputRef;

    if (current && selected) {
      current.value = selected[keyName];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    document.addEventListener("click", onclick);

    return () => {
      document.removeEventListener("click", onclick);
    };
  });

  return (
    <section className={className} id={id}>
      <TextInput
        {...props}
        onUpdate={undefined}
        ref={inputRef}
        autocomplete={TextInputAutocompleteEnum.OFF}
        minLength={undefined}
        placeholder={placeholder}
        size={undefined}
        spellcheck={false}
        type={TextInputTypeEnum.SEARCH}
        value={value}
        onBlur={onblur}
        onFocus={onfocus}
        onInput={oninput}
        onKeyDown={onkeydown}
      />

      {renderNoResults()}

      {renderResults()}
    </section>
  );
}

export default Autocomplete;
export { Autocomplete, type AutocompleteMeta, type AutocompleteProps };
export type { AutocompleteResultType, AutocompleteResultsType };
